// Time-scope windowing for receipt pages.
//
// All comparisons are on the operational intake_date (YYYY-MM-DD). Those strings
// sort lexically in chronological order, so range checks are plain string
// comparisons — no Date parsing, no timezone drift.
//
// Week = current calendar week starting Monday. Month = current calendar month
// from the 1st. Both run through "today" (no future bound).

export type TimeWindow = 'today' | 'week' | 'month' | 'custom';

export interface ScopeValue {
  window: TimeWindow;
  from?: string; // YYYY-MM-DD (custom only)
  to?: string;   // YYYY-MM-DD (custom only)
}

export interface WindowBounds {
  start: string | null; // inclusive YYYY-MM-DD; null = open-ended
  end: string | null;   // inclusive YYYY-MM-DD; null = open-ended
}

const pad = (n: number) => String(n).padStart(2, '0');
const toIso = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export function todayIso(): string {
  return toIso(new Date());
}

// Monday of the calendar week containing `ref` (local time).
function weekStartIso(ref: Date): string {
  const d = new Date(ref);
  const daysSinceMonday = (d.getDay() + 6) % 7; // getDay: 0=Sun..6=Sat
  d.setDate(d.getDate() - daysSinceMonday);
  return toIso(d);
}

// First day of the calendar month containing `ref` (local time).
function monthStartIso(ref: Date): string {
  return `${ref.getFullYear()}-${pad(ref.getMonth() + 1)}-01`;
}

// `referenceToday` overrides "today" for datasets pinned to a fixed demo date
// (e.g. Proxy's PROXY_SEED_TODAY) rather than the real calendar. Defaults to
// the actual current date for everything else.
export function windowBounds(scope: ScopeValue, referenceToday?: string): WindowBounds {
  const today = referenceToday ?? todayIso();
  const refDate = new Date(today + 'T00:00:00');
  switch (scope.window) {
    case 'today':  return { start: today, end: today };
    case 'week':   return { start: weekStartIso(refDate), end: today };
    case 'month':  return { start: monthStartIso(refDate), end: today };
    case 'custom': return { start: scope.from || null, end: scope.to || null };
  }
}

// Null intake_date is never in any window.
export function inWindow(intakeDate: string | null, b: WindowBounds): boolean {
  if (!intakeDate) return false;
  if (b.start && intakeDate < b.start) return false;
  if (b.end && intakeDate > b.end) return false;
  return true;
}

export interface WindowCounts {
  today: number;
  week: number;
  month: number;
}

// Row counts for each live preset (nested: today ⊆ week ⊆ month).
export function windowCounts(rows: { intake_date: string | null }[], referenceToday?: string): WindowCounts {
  const t = windowBounds({ window: 'today' }, referenceToday);
  const w = windowBounds({ window: 'week' }, referenceToday);
  const m = windowBounds({ window: 'month' }, referenceToday);
  let today = 0, week = 0, month = 0;
  for (const r of rows) {
    if (inWindow(r.intake_date, t)) today++;
    if (inWindow(r.intake_date, w)) week++;
    if (inWindow(r.intake_date, m)) month++;
  }
  return { today, week, month };
}
